import React, { useEffect, useMemo, useState } from "react";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "@/components/ui/button";
import { cn } from "@/utils/utils";
import { CourseContentBlock } from "@/modules/learningCenter/features/CourseDirectory/types/viewer";
import {
  gradeQuestion,
  parseKnowledgeCheck,
  scoreKnowledgeCheck,
  type KnowledgeCheckAnswers,
} from "@/modules/learningCenter/features/CourseDirectory/lib/knowledgeCheck";

interface KnowledgeCheckPlayerProps {
  block: CourseContentBlock;
}

interface PersistedState {
  answers: KnowledgeCheckAnswers;
  submitted: boolean;
}

const EMPTY_STATE: PersistedState = { answers: {}, submitted: false };

function storageKey(uuid: string): string {
  return `kc-progress:${uuid}`;
}

function loadPersisted(uuid: string): PersistedState {
  try {
    const raw = window.localStorage.getItem(storageKey(uuid));
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    const answers =
      parsed.answers && typeof parsed.answers === "object"
        ? (parsed.answers as KnowledgeCheckAnswers)
        : {};
    return { answers, submitted: parsed.submitted === true };
  } catch {
    return EMPTY_STATE;
  }
}

/**
 * Learner-facing knowledge check. Renders questions with radio options and a
 * "Check answers" button; on submit it grades each question, reveals the
 * correct option and explanation, and shows a score. Progress is persisted to
 * `localStorage` (keyed by the block uuid) and rehydrated on mount.
 *
 * This is a self-contained "quick check": it never calls an API and never
 * affects course or module progress.
 */
export function KnowledgeCheckPlayer({ block }: KnowledgeCheckPlayerProps) {
  const content = useMemo(
    () => parseKnowledgeCheck(block.metadata),
    [block.metadata],
  );

  const [answers, setAnswers] = useState<KnowledgeCheckAnswers>(
    () => loadPersisted(block.uuid).answers,
  );
  const [submitted, setSubmitted] = useState<boolean>(
    () => loadPersisted(block.uuid).submitted,
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(
        storageKey(block.uuid),
        JSON.stringify({ answers, submitted }),
      );
    } catch {
      // localStorage may be unavailable (private mode / quota) — progress is
      // best-effort and must never break the learner view.
    }
  }, [block.uuid, answers, submitted]);

  if (content.questions.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400">
        This knowledge check has no questions yet.
      </p>
    );
  }

  const score = scoreKnowledgeCheck(content.questions, answers);

  const selectAnswer = (questionId: string, optionId: string) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [questionId]: optionId }));
  };

  const handleSubmit = () => setSubmitted(true);

  const handleReset = () => {
    setAnswers({});
    setSubmitted(false);
  };

  return (
    <div className="my-6 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h3 className="text-base font-normal text-gray-900 dark:text-white">
          {content.title ?? "Knowledge check"}
        </h3>
      </div>
      <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
        Quick check — this does not affect your progress.
      </p>

      <div className="space-y-5">
        {content.questions.map((question, qIndex) => {
          const result = gradeQuestion(question, answers[question.id]);
          return (
            <fieldset className="space-y-2" key={question.id}>
              <legend className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                <span className="tabular-nums text-gray-500 dark:text-gray-400">
                  {qIndex + 1}.
                </span>
                <span>{question.prompt}</span>
                {submitted && result === "correct" && (
                  <CheckCircleIcon
                    aria-label="Correct"
                    className="h-4 w-4 text-green-600 dark:text-green-400"
                  />
                )}
                {submitted && result !== "correct" && (
                  <XCircleIcon
                    aria-label="Incorrect"
                    className="h-4 w-4 text-red-600 dark:text-red-400"
                  />
                )}
              </legend>

              <div className="space-y-1.5 pl-5">
                {question.options.map((option) => {
                  const selected = answers[question.id] === option.id;
                  const revealCorrect = submitted && option.correct === true;
                  return (
                    <label
                      className={cn(
                        "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors",
                        revealCorrect
                          ? "border-green-500 bg-green-50 dark:border-green-500/60 dark:bg-green-500/10 text-gray-900 dark:text-white"
                          : "border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-200",
                        submitted
                          ? "cursor-default"
                          : "hover:bg-gray-50 dark:hover:bg-slate-700/50",
                      )}
                      key={option.id}
                    >
                      <input
                        checked={selected}
                        disabled={submitted}
                        name={`kc-${block.uuid}-${question.id}`}
                        onChange={() => selectAnswer(question.id, option.id)}
                        type="radio"
                        value={option.id}
                      />
                      <span className="flex-1">{option.label}</span>
                    </label>
                  );
                })}
              </div>

              {submitted && question.explanation && (
                <p className="pl-5 text-sm text-gray-500 dark:text-gray-400">
                  {question.explanation}
                </p>
              )}
            </fieldset>
          );
        })}
      </div>

      <div className="mt-5 flex items-center gap-3">
        {submitted ? (
          <>
            <span className="text-sm text-gray-900 dark:text-white">
              You scored {score.correct} / {score.total}
            </span>
            <Button onClick={handleReset} size="sm" variant="outline">
              Try again
            </Button>
          </>
        ) : (
          <Button onClick={handleSubmit} size="sm">
            Check answers
          </Button>
        )}
      </div>
    </div>
  );
}

export default KnowledgeCheckPlayer;
